/**
 * @fileoverview Non-sticky footer component for help pages
 * 
 * This component provides a consistent footer for all help pages with the same
 * content as the main Footer but without fixed positioning.
 */

import React from 'react';
import { Facebook, Twitter, Instagram, Youtube, X, HelpCircle, MessageSquare, FileQuestion, Phone, FileText, Shield } from 'lucide-react';

export const HelpPageFooter: React.FC = () => {
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 480;

  return (
    <footer className="bg-[#1e2434] border-t border-gray-700 px-6 py-2 mt-auto">
      <div className="max-w-6xl mx-auto">
        {/* Mobile layout: Social on top, navigation below */}
        {isMobile && (
          <div className="flex flex-col items-center space-y-2">
                         {/* Social media links section */}
             <div className="flex space-x-2">
               <a href="https://www.facebook.com/spinnergames.fun" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors">
                 <Facebook className="h-3.5 w-3.5 text-white" />
               </a>
               <a href="https://www.instagram.com/spinnergames.fun" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-pink-600 hover:bg-pink-700 rounded-full transition-colors">
                 <Instagram className="h-3.5 w-3.5 text-white" />
               </a>
               <a href="https://www.youtube.com/@SpinnerGamesFun" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-red-600 hover:bg-red-700 rounded-full transition-colors">
                 <Youtube className="h-3.5 w-3.5 text-white" />
               </a>
               <a href="https://x.com/SpinnerGamesFun" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-black hover:bg-gray-800 rounded-full transition-colors border border-gray-600">
                 <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                   <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" transform="scale(1.2) translate(-2, -2)"/>
                 </svg>
               </a>
               <a href="https://www.tiktok.com/@spinner.games" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-black hover:bg-gray-800 rounded-full transition-colors border border-gray-600">
                 <img src="/images/tiktok.png" alt="TikTok" className="h-3.5 w-3.5" style={{ filter: 'brightness(0) invert(1)' }} />
               </a>
             </div>

            {/* Navigation and help buttons section */}
            <div className="flex justify-center space-x-1">
              <a href="https://spinnergames.fun/blogs/testimonials/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-1 py-1 text-xs text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                <MessageSquare className="h-3 w-3 mr-1" />
                Feedback
              </a>
              <a href="/help" className="inline-flex items-center px-1 py-1 text-xs text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                <HelpCircle className="h-3 w-3 mr-1" />
                Help Center
              </a>
              <a href="/help/terms" className="inline-flex items-center px-1 py-1 text-xs text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                <FileText className="h-3 w-3 mr-1" />
                Terms
              </a>
              <a href="/help/privacy-policy" className="inline-flex items-center px-1 py-1 text-xs text-gray-300 hover:text-white transition-colors whitespace-nowrap">
                <Shield className="h-3 w-3 mr-1" />
                Privacy
              </a>
            </div>
          </div>
        )}

        {/* Desktop layout: Social left, navigation right */}
        {!isMobile && (
          <div className="flex items-center justify-between">
                         {/* Social media links section */}
             <div className="flex items-center space-x-4">
               <div className="flex space-x-2">
                 <a href="https://www.facebook.com/spinnergames.fun" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-blue-600 hover:bg-blue-700 rounded-full transition-colors">
                   <Facebook className="h-3.5 w-3.5 text-white" />
                 </a>
                 <a href="https://www.instagram.com/spinnergames.fun" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-pink-600 hover:bg-pink-700 rounded-full transition-colors">
                   <Instagram className="h-3.5 w-3.5 text-white" />
                 </a>
                 <a href="https://www.youtube.com/@SpinnerGamesFun" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-red-600 hover:bg-red-700 rounded-full transition-colors">
                   <Youtube className="h-3.5 w-3.5 text-white" />
                 </a>
                 <a href="https://x.com/SpinnerGamesFun" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-black hover:bg-gray-800 rounded-full transition-colors border border-gray-600">
                   <svg className="h-3.5 w-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                     <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" transform="scale(1.2) translate(-2, -2)"/>
                   </svg>
                 </a>
                 <a href="https://www.tiktok.com/@spinner.games" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 bg-black hover:bg-gray-800 rounded-full transition-colors border border-gray-600">
                   <img src="/images/tiktok.png" alt="TikTok" className="h-3.5 w-3.5" style={{ filter: 'brightness(0) invert(1)' }} />
                 </a>
               </div>
             </div>

            {/* Navigation and help buttons section */}
            <div className="flex items-center space-x-2">
              <a href="https://spinnergames.fun/blogs/testimonials/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-2 py-1 text-sm text-gray-300 hover:text-white transition-colors">
                <MessageSquare className="h-4 w-4 mr-1" />
                Feedback
              </a>
              <a href="/help" className="inline-flex items-center px-2 py-1 text-sm text-gray-300 hover:text-white transition-colors">
                <HelpCircle className="h-4 w-4 mr-1" />
                Help Center
              </a>
              <a href="/help/terms" className="inline-flex items-center px-2 py-1 text-sm text-gray-300 hover:text-white transition-colors">
                <FileText className="h-4 w-4 mr-1" />
                Terms
              </a>
              <a href="/help/privacy-policy" className="inline-flex items-center px-2 py-1 text-sm text-gray-300 hover:text-white transition-colors">
                <Shield className="h-4 w-4 mr-1" />
                Privacy
              </a>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}; 